import { cn } from "@-/com.npmjs.cn";
import { Platform, Pressable, View, type AccessibilityRole } from "@-/com.npmjs.react-native";
import { Button, type ButtonProps } from "@-/dev.improve.haniverse.components.ui.button";
import { Icon, type IconName } from "@-/dev.improve.haniverse.components.ui.icon";
import { Switch } from "@-/dev.improve.haniverse.components.ui.switch";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import type { ReactNode } from "react";
import { useToolbarPopover } from "#toolbar-popover-context";

// Pressing or checking a chip uses the same neutral accent gray as other app controls.
const CHIP_PRESS_OVERRIDE = Platform.select({
  default: "active:bg-accent",
  web: "active:bg-accent hover:bg-accent",
});

/** A titled group of popover controls — the popover's equivalent of a menu section. */
export function ToolbarPopoverSection({
  children,
  className,
  title,
}: {
  children: ReactNode;
  className?: string;
  title?: string;
}) {
  return (
    <View className={cn("gap-2", className)}>
      {title === undefined ? null : <Text className="text-xs text-muted-foreground">{title}</Text>}
      {children}
    </View>
  );
}

/** A labelled row with its control (whatever `children` is) at the trailing edge. */
export function ToolbarPopoverRow({
  children,
  className,
  description,
  label,
  testID,
}: {
  children?: ReactNode;
  className?: string;
  description?: string;
  label: string;
  testID?: string;
}) {
  return (
    <View
      className={cn("min-h-9 flex-row items-center justify-between gap-3", className)}
      testID={testID}
    >
      <View className="min-w-0 shrink">
        <Text className="text-sm text-foreground">{label}</Text>
        {description === undefined ? null : (
          <Text className="text-xs text-muted-foreground">{description}</Text>
        )}
      </View>
      {children}
    </View>
  );
}

export interface ToolbarPopoverSwitchRowProps {
  checked: boolean;
  description?: string;
  disabled?: boolean;
  label: string;
  onCheckedChange: (checked: boolean) => void;
  testID?: string;
}

/**
 * An on/off setting as a labelled switch. The whole row is the control (one accessibility element
 * with the `switch` role, addressable by its `testID` and named by its visible label), so the tap
 * target is the full width rather than the 32pt thumb; the drawn `Switch` is purely visual.
 */
export function ToolbarPopoverSwitchRow({
  checked,
  description,
  disabled = false,
  label,
  onCheckedChange,
  testID,
}: ToolbarPopoverSwitchRowProps) {
  return (
    <Pressable
      accessibilityRole="switch"
      accessibilityState={{ checked, disabled }}
      className={cn(
        "min-h-9 flex-row items-center justify-between gap-3 rounded-xs active:opacity-70",
        disabled && "opacity-50",
      )}
      disabled={disabled}
      onPress={() => onCheckedChange(!checked)}
      testID={testID}
    >
      <View className="min-w-0 shrink">
        <Text className="text-sm text-foreground">{label}</Text>
        {description === undefined ? null : (
          <Text className="text-xs text-muted-foreground">{description}</Text>
        )}
      </View>
      <View accessible={false} importantForAccessibility="no-hide-descendants" pointerEvents="none">
        <Switch checked={checked} disabled={disabled} onCheckedChange={onCheckedChange} />
      </View>
    </Pressable>
  );
}

/** Wraps chips so they flow onto further lines when they outgrow the popover's width. */
export function ToolbarPopoverChipGroup({
  accessibilityRole,
  children,
  className,
}: {
  accessibilityRole?: AccessibilityRole;
  children: ReactNode;
  className?: string;
}) {
  return (
    <View
      accessibilityRole={accessibilityRole}
      className={cn("flex-row flex-wrap items-center gap-1.5", className)}
    >
      {children}
    </View>
  );
}

export interface ToolbarPopoverChipProps {
  /** `radio` for one-of-many choices (the default), `checkbox` for any-of-many filters. */
  accessibilityRole?: "checkbox" | "radio";
  checked: boolean;
  disabled?: boolean;
  label: string;
  onPress: () => void;
  testID?: string;
}

/**
 * A choice pill: the accent fill marks the checked one, exactly like the toolbar's view pills, so
 * the popover's options read in the same visual language as the row they opened from.
 */
export function ToolbarPopoverChip({
  accessibilityRole = "radio",
  checked,
  disabled = false,
  label,
  onPress,
  testID,
}: ToolbarPopoverChipProps) {
  return (
    <Pressable
      accessibilityRole={accessibilityRole}
      accessibilityState={{ checked, disabled, selected: checked }}
      className={cn(
        "h-8 min-w-14 items-center justify-center rounded-full border border-border/60 px-3.5",
        checked ? "bg-accent" : "bg-background",
        disabled && "opacity-50",
        CHIP_PRESS_OVERRIDE,
      )}
      disabled={disabled}
      onPress={onPress}
      testID={testID}
    >
      <Text
        className={
          checked ? "text-xs font-medium text-accent-foreground" : "text-xs text-muted-foreground"
        }
      >
        {label}
      </Text>
    </Pressable>
  );
}

export interface ToolbarPopoverChoiceOption<Value extends string> {
  label: string;
  testID?: string;
  value: Value;
}

export interface ToolbarPopoverChoiceRowProps<Value extends string> {
  /** Collapse the popover once a choice is made — for a sort order, where the choice is the point. */
  closeOnSelect?: boolean;
  label: string;
  /**
   * `inline` keeps the chips beside the label; `stacked` puts them on their own wrapping line under
   * it. Defaults to `inline` for up to two options and `stacked` beyond that.
   */
  layout?: "inline" | "stacked";
  onValueChange: (value: Value) => void;
  options: readonly ToolbarPopoverChoiceOption<Value>[];
  value: Value;
}

/** A one-of-many setting as a labelled row of radio chips. */
export function ToolbarPopoverChoiceRow<Value extends string>({
  closeOnSelect = false,
  label,
  layout,
  onValueChange,
  options,
  value,
}: ToolbarPopoverChoiceRowProps<Value>) {
  const { close } = useToolbarPopover();
  const resolvedLayout = layout ?? (options.length > 2 ? "stacked" : "inline");
  const chips = (
    <ToolbarPopoverChipGroup
      accessibilityRole="radiogroup"
      className={resolvedLayout === "inline" ? "shrink justify-end" : undefined}
    >
      {options.map((option) => (
        <ToolbarPopoverChip
          checked={option.value === value}
          key={option.value}
          label={option.label}
          onPress={() => {
            onValueChange(option.value);
            if (closeOnSelect) {
              close();
            }
          }}
          testID={option.testID}
        />
      ))}
    </ToolbarPopoverChipGroup>
  );
  if (resolvedLayout === "stacked") {
    return <ToolbarPopoverSection title={label}>{chips}</ToolbarPopoverSection>;
  }
  return <ToolbarPopoverRow label={label}>{chips}</ToolbarPopoverRow>;
}

export interface ToolbarPopoverActionButtonProps extends Pick<
  ButtonProps,
  "disabled" | "isLoading" | "testID" | "variant"
> {
  /**
   * Collapse the popover as `onPress` runs (the default). The press runs the action at once; the
   * menu's modal goes in the same commit, so a sheet, dialog or toast the action presents is
   * never behind it. Pass `false` for an action whose result should be seen inside the popover
   * (a reset).
   */
  closeOnPress?: boolean;
  label: string;
  name?: IconName;
  /** Omit for a button that only closes the popover (a "Done"). */
  onPress?: () => void;
}

/** A one-shot action as a full-width button row. */
export function ToolbarPopoverActionButton({
  closeOnPress = true,
  disabled,
  isLoading,
  label,
  name,
  onPress,
  testID,
  variant = "outline",
}: ToolbarPopoverActionButtonProps) {
  const { close } = useToolbarPopover();
  return (
    <Button
      className="h-9 justify-start gap-2 rounded-full px-3.5"
      disabled={disabled}
      isLoading={isLoading}
      onPress={() => {
        // The action never waits on the collapse, so neither an interrupted animation nor the
        // popover unmounting mid-close can drop it.
        if (closeOnPress) {
          close();
        }
        onPress?.();
      }}
      size="sm"
      testID={testID}
      variant={variant}
    >
      {name === undefined ? null : <Icon className="size-4 text-foreground" name={name} />}
      <Text>{label}</Text>
    </Button>
  );
}

export function ToolbarPopoverSeparator() {
  return <View className="h-px bg-border" />;
}
